import React, { useState, useEffect, useRef } from 'react';
import QRCode from 'qrcode';
import { copyToClipboard } from '../utils/storage';
import { formatFileSize } from '../utils/api';
import CheckoutModal from './CheckoutModal';
import { Check, Copy } from 'lucide-react';

function ShareLink({ shareLink, filename, fileCount, totalSize, uploadStatus, folderId, expiryDays, onNewUpload }) {
  const [copied, setCopied] = useState(false);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState('');
  const [showQR, setShowQR] = useState(true);
  const [isUserPremium, setIsUserPremium] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [selectedTier, setSelectedTier] = useState('basic');
  const [foreverLocked, setForeverLocked] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [lockingForever, setLockingForever] = useState(false);

  // Generate QR Code when component mounts
  useEffect(() => {
    const generateQRCode = async () => {
      try {
        const dataUrl = await QRCode.toDataURL(shareLink, {
          width: 200,
          margin: 2,
          color: {
            dark: '#1e293b',  // Dark color for QR code
            light: '#ffffff'  // Light background for QR code
          }
        });
        setQrCodeDataUrl(dataUrl);
      } catch (error) {
        console.error('Error generating QR code:', error);
      }
    };

    if (shareLink) {
      generateQRCode();
    }
  }, [shareLink]);

  // Check if current user has an active paid subscription
  useEffect(() => {
    const checkUserSubscription = async () => {
      try {
        const userId = localStorage.getItem('fileshare_user_id');
        if (!userId) {
          setIsUserPremium(false);
          setForeverLocked(false);
          return;
        }
        const apiBase = '';
        const res = await fetch(`${apiBase}/api/user/storage?userId=${encodeURIComponent(userId)}`);
        if (res.ok) {
          const data = await res.json();
          const tier = data?.subscriptionTier?.tier;
          const active = Boolean(tier && tier !== 'free' && data?.subscriptionTier?.status === 'active');
          if (active) {
            setIsUserPremium(true);
            setForeverLocked(true);

            // Auto-lock forever retention on the backend for active subscribers
            const fId = folderId || (shareLink ? shareLink.split('/d/')[1]?.split('?')[0] : null);
            if (fId) {
              fetch(`${apiBase}/api/folder/${fId}/set-forever`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ userId })
              }).catch(() => {});
            }
          } else {
            setIsUserPremium(false);
            setForeverLocked(false);
          }
        } else {
          setIsUserPremium(false);
          setForeverLocked(false);
        }
      } catch (err) {
        console.warn('Subscription check error:', err);
        setIsUserPremium(false);
        setForeverLocked(false);
      }
    };
    checkUserSubscription();
  }, [folderId, shareLink]);

  const handleCopy = async () => {
    const success = await copyToClipboard(shareLink);
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleDownloadQR = () => {
    if (qrCodeDataUrl) {
      const link = document.createElement('a');
      link.download = `qr-code-${filename || 'share'}.png`;
      link.href = qrCodeDataUrl;
      link.click();
    }
  };

  const handleCheckoutSuccess = async () => {
    setIsUserPremium(true);
    setShowCheckout(false);
    setForeverLocked(true);
    const fId = folderId || (shareLink ? shareLink.split('/d/')[1]?.split('?')[0] : null);
    const userId = localStorage.getItem('fileshare_user_id');
    if (fId && userId) {
      try {
        const apiBase = '';
        await fetch(`${apiBase}/api/folder/${fId}/set-forever`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId })
        });
      } catch (_) {}
    }
  };

  return (
    <div style={{ 
      backgroundColor: '#12141a', 
      border: '1px solid #1f232e',
      borderRadius: '8px',
      padding: '24px',
      overflow: 'hidden',
      maxWidth: '100%',
      boxSizing: 'border-box'
    }}>
      {/* Success Header */}
      <div style={{ textAlign: 'center', marginBottom: '20px' }}>
        <div style={{ 
          backgroundColor: 'rgba(16, 185, 129, 0.1)', 
          borderRadius: '50%', 
          width: '46px', 
          height: '46px', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          margin: '0 auto 14px',
          border: '1px solid rgba(16, 185, 129, 0.25)'
        }}>
          <Check size={22} color="#10b981" />
        </div>
        
        <h3 style={{ 
          fontSize: '18px', 
          fontWeight: '600', 
          color: '#ffffff', 
          marginBottom: '6px' 
        }}>
          Upload Successful!
        </h3>
        
        <p style={{ color: '#9ca3af', fontSize: '13px', margin: 0 }}>
          {fileCount && fileCount > 1
            ? `${fileCount} files (${formatFileSize(totalSize)}) uploaded successfully.`
            : 'Your file(s) has been uploaded successfully.'}
        </p>
      </div>

      {/* Share Link Section */}
      <div style={{ 
        backgroundColor: '#181a22', 
        border: '1px solid #242834',
        borderRadius: '6px',
        padding: '14px', 
        marginBottom: '16px',
        overflow: 'hidden'
      }}>
        <label style={{ 
          display: 'block', 
          fontSize: '12px', 
          fontWeight: '500', 
          color: '#ffffff', 
          marginBottom: '8px' 
        }}>
          Share Link
        </label>
        
        <div style={{ display: 'flex', width: '100%', minWidth: 0 }}>
          <input
            type="text"
            value={shareLink}
            readOnly
            style={{
              flex: 1,
              minWidth: 0,
              padding: '9px 12px',
              backgroundColor: '#12141a',
              border: '1px solid #282d3b',
              borderRight: 'none',
              borderRadius: '6px 0 0 6px',
              color: '#ffffff',
              fontSize: '12px',
              outline: 'none',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          />
          
          <button
            onClick={handleCopy}
            style={{
              padding: '9px 16px',
              backgroundColor: copied ? '#10b981' : '#ffffff',
              border: 'none',
              borderRadius: '0 6px 6px 0',
              color: copied ? '#ffffff' : '#0a0b0d',
              fontSize: '12px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.15s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              flexShrink: 0
            }}
            onMouseEnter={(e) => {
              if (!copied) {
                e.target.style.backgroundColor = '#e4e4e7';
              }
            }}
            onMouseLeave={(e) => {
              if (!copied) {
                e.target.style.backgroundColor = '#ffffff';
              }
            }}
          >
            {copied ? (
              <>
                <Check size={14} />
                Copied!
              </>
            ) : (
              <>
                <Copy size={14} />
                Copy
              </>
            )}
          </button>
        </div>
      </div>

      {/* FOREVER RETENTION OPTION (Post-Upload) */}
      {isUserPremium && foreverLocked ? (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
          backgroundColor: '#181a22',
          border: '1px solid #242834',
          borderRadius: '6px',
          padding: '12px 14px',
          marginBottom: '14px'
        }}>
          <div style={{ color: '#ffffff', fontSize: '12px', fontWeight: '600' }}>
            Link stays active
          </div>
          <span style={{ color: '#a1a6b2', fontSize: '11px' }}>Paid plan active</span>
        </div>
      ) : (
        <div style={{
          backgroundColor: '#181a22',
          border: '1px solid #242834',
          borderRadius: '6px',
          padding: '12px 14px',
          marginBottom: '14px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', marginBottom: '9px' }}>
            <span style={{ fontSize: '12px', fontWeight: '600', color: '#ffffff' }}>
              Keep files longer
            </span>
            <span style={{ fontSize: '11px', color: '#8b909c', whiteSpace: 'nowrap' }}>
              Free links expire in 24h
            </span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap' }}>
            <span style={{ color: '#9ca3af', fontSize: '11px', lineHeight: 1.4 }}>
              Stay online while subscribed.
            </span>
            <button
              type="button"
              onClick={() => {
                setSelectedTier('basic');
                setShowCheckout(true);
              }}
              style={{
                backgroundColor: '#ffffff',
                color: '#0a0b0d',
                border: 'none',
                borderRadius: '5px',
                padding: '7px 10px',
                fontSize: '11px',
                fontWeight: '600',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                transition: 'background-color 0.15s ease'
              }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#e4e4e7'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#ffffff'; }}
            >
              Upgrade · $5/mo
            </button>
          </div>
        </div>
      )}

      {/* QR Code Section */}
      <div style={{ 
        backgroundColor: '#181a22', 
        border: '1px solid #242834',
        borderRadius: '6px',
        padding: '14px', 
        marginBottom: '16px'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <label style={{ 
            fontSize: '12px', 
            fontWeight: '500', 
            color: '#ffffff'
          }}>
            QR Code
          </label>
          <button
            onClick={() => setShowQR(!showQR)}
            style={{
              backgroundColor: 'transparent',
              color: '#9ca3af',
              border: 'none',
              fontSize: '12px',
              cursor: 'pointer',
              textDecoration: 'underline'
            }}
            onMouseEnter={(e) => e.target.style.color = '#ffffff'}
            onMouseLeave={(e) => e.target.style.color = '#9ca3af'}
          >
            {showQR ? 'Hide' : 'Show'} QR Code
          </button>
        </div>

        {showQR && (
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center',
            textAlign: 'center' 
          }}>
            {qrCodeDataUrl ? (
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center' 
              }}>
                <img 
                  src={qrCodeDataUrl} 
                  alt="QR Code for share link"
                  style={{ 
                    width: '180px', 
                    height: '180px',
                    backgroundColor: '#fff',
                    padding: '8px',
                    borderRadius: '6px',
                    marginBottom: '12px',
                    display: 'block',
                    margin: '0 auto 12px auto'
                  }}
                />
                <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                  <button
                    onClick={handleDownloadQR}
                    style={{
                      backgroundColor: '#1b1e27',
                      color: '#e4e4e7',
                      border: '1px solid #282d3b',
                      padding: '7px 14px',
                      borderRadius: '6px',
                      fontSize: '11px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#222632';
                      e.target.style.borderColor = '#374151';
                      e.target.style.color = '#ffffff';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = '#1b1e27';
                      e.target.style.borderColor = '#282d3b';
                      e.target.style.color = '#e4e4e7';
                    }}
                  >
                    Download QR Code
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ color: '#9ca3af', fontSize: '12px' }}>
                Generating QR code...
              </div>
            )}
          </div>
        )}
      </div>

      {/* Value Reassurance */}
      <div style={{
        margin: '12px 0 16px',
        padding: '12px 2px',
        borderTop: '1px solid #242834',
        borderBottom: '1px solid #242834',
        fontSize: '11px',
        color: '#9ca3af',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '8px'
      }}>
        <span style={{ color: '#d1d5db', fontWeight: '500' }}>
          Unlimited downloads · no recipient fees
        </span>
        <a href="/pricing" style={{ color: '#aeb4c0', textDecoration: 'none', fontWeight: '500' }}>
          Premium · up to 1 TB →
        </a>
      </div>
      {/* Action Buttons */}
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
        <button
          onClick={() => window.open(shareLink, '_blank')}
          style={{
            flex: '1 1 auto',
            minWidth: '120px',
            backgroundColor: '#ffffff',
            color: '#0a0b0d',
            padding: '10px 16px',
            border: 'none',
            borderRadius: '6px',
            fontSize: '12px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'background-color 0.15s ease'
          }}
          onMouseEnter={(e) => e.target.style.backgroundColor = '#e4e4e7'}
          onMouseLeave={(e) => e.target.style.backgroundColor = '#ffffff'}
        >
          View Files
        </button>
        
        <button
          onClick={onNewUpload}
          style={{
            flex: '1 1 auto',
            minWidth: '120px',
            backgroundColor: '#1b1e27',
            color: '#e4e4e7',
            border: '1px solid #282d3b',
            padding: '10px 16px',
            borderRadius: '6px',
            fontSize: '12px',
            fontWeight: '500',
            cursor: 'pointer',
            transition: 'all 0.15s ease'
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#222632';
            e.target.style.borderColor = '#374151';
            e.target.style.color = '#ffffff';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = '#1b1e27';
            e.target.style.borderColor = '#282d3b';
            e.target.style.color = '#e4e4e7';
          }}
        >
          Upload Another
        </button>
      </div>

      {/* Dodo Payments Checkout Modal */}
      {showCheckout && (
        <CheckoutModal
          isOpen={showCheckout}
          onClose={() => setShowCheckout(false)}
          selectedTier={selectedTier}
          onSuccess={handleCheckoutSuccess}
        />
      )}
    </div>
  );
}

export default ShareLink;
